/**
 * SMS Inbox — Threaded Conversation View
 * Left panel: contact list sorted by most recent message
 * Right panel: full chronological chat-bubble thread for selected contact
 * Reply box at bottom of thread
 *
 * Compliance: All outbound messages automatically append
 * "Reply STOP to opt-out" per Twilio A2P 10DLC requirements.
 */
import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  RefreshCw, Paperclip, FileText, ExternalLink, Send,
  PenSquare, X, ImageIcon, Search, MessageSquare, Phone, Trash2, UserPlus
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { SmsComposeModal } from "@/components/SmsComposeModal";

// ─── Compliance footer ────────────────────────────────────────────────────────
const OPT_OUT_FOOTER = "\n\nReply STOP to opt-out.";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(date: Date | string) {
  const d = new Date(date);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return d.toLocaleDateString("en-US", { weekday: "short" });
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatBubbleTime(date: Date | string) {
  return new Date(date).toLocaleString("en-US", {
    month: "short", day: "numeric",
    hour: "numeric", minute: "2-digit", hour12: true,
  });
}

function isImageUrl(url: string): boolean {
  return /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?|$)/i.test(url);
}

function isPdfUrl(url: string): boolean {
  return /\.pdf(\?|$)/i.test(url);
}

/** Extract MMS filename from body string like "Hello [MMS: file.pdf]" */
function parseMmsBody(body: string): { text: string; mmsFilename: string | null } {
  const match = body.match(/\[MMS: ([^\]]+)\]/);
  if (match) {
    return { text: body.replace(/\[MMS: [^\]]+\]/, "").trim(), mmsFilename: match[1] };
  }
  return { text: body, mmsFilename: null };
}

// ─── Types ────────────────────────────────────────────────────────────────────

type CombinedMessage = {
  id: string;
  direction: "sent" | "received";
  contactName: string | null;
  phoneOrEmail: string | null;
  body: string;
  senderName: string | null;
  timestamp: Date | string;
  mediaUrl?: string | null;
  source?: string | null;
  /** Subject from client_notifications — contains "SMS [APXGS]" or "SMS [HRBBS]" for sent messages */
  subject?: string | null;
};

type Conversation = {
  contactKey: string;
  displayName: string;
  phone: string | null;
  messages: CombinedMessage[];
  lastMessage: CombinedMessage;
  unreadCount: number;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function MediaAttachment({ url }: { url: string }) {
  const [imgError, setImgError] = useState(false);

  if (isImageUrl(url) && !imgError) {
    return (
      <div className="mt-2">
        <div className="rounded-xl overflow-hidden" style={{ maxWidth: "260px", border: "1px solid oklch(0.80 0.02 240)" }}>
          <img
            src={url} alt="MMS attachment"
            className="w-full h-auto block"
            style={{ maxHeight: "220px", objectFit: "contain", background: "oklch(0.94 0.006 240)" }}
            onError={() => setImgError(true)}
          />
        </div>
        <a href={url} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 mt-1 text-xs font-medium"
          style={{ color: "oklch(0.35 0.14 240)" }}>
          <ExternalLink size={10} /> Open full size
        </a>
      </div>
    );
  }

  if (isPdfUrl(url)) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer"
        className="inline-flex items-center gap-2 mt-2 px-3 py-2 rounded-lg text-xs font-semibold"
        style={{ background: "oklch(0.90 0.01 240)", border: "1px solid oklch(0.76 0.02 240)", color: "oklch(0.10 0.04 240)" }}>
        <FileText size={13} style={{ color: "oklch(0.42 0.14 25)" }} />
        View PDF
        <ExternalLink size={11} style={{ color: "oklch(0.45 0.04 240)" }} />
      </a>
    );
  }

  return (
    <a href={url} target="_blank" rel="noopener noreferrer"
      className="inline-flex items-center gap-2 mt-2 px-3 py-2 rounded-lg text-xs font-semibold"
      style={{ background: "oklch(0.90 0.01 240)", border: "1px solid oklch(0.76 0.02 240)", color: "oklch(0.10 0.04 240)" }}>
      <Paperclip size={13} />
      View Attachment
      <ExternalLink size={11} style={{ color: "oklch(0.45 0.04 240)" }} />
    </a>
  );
}

/** Single chat bubble */
function ChatBubble({ msg }: { msg: CombinedMessage }) {
  const isOut = msg.direction === "sent";
  const { text, mmsFilename } = parseMmsBody(msg.body || "");

  return (
    <div
      className="flex flex-col mb-4"
      style={{ alignItems: isOut ? "flex-end" : "flex-start" }}
    >
      {/* Sender label */}
      <span className="mb-1 px-1" style={{ fontSize: "11px", fontWeight: 800, letterSpacing: "0.04em", color: isOut ? "#B8922A" : "#3d3328", textTransform: "uppercase" }}>
        {isOut ? (msg.senderName || "APX Team") : (msg.contactName || msg.phoneOrEmail || "Client")}
      </span>

      {/* Bubble */}
      <div
        className="rounded-2xl px-4 py-3"
        style={{
          maxWidth: "72%",
          wordBreak: "break-word",
          whiteSpace: "pre-wrap",
          fontSize: "15px",
          lineHeight: "1.6",
          fontWeight: 500,
          ...(isOut
            ? {
                background: "oklch(0.82 0.07 75)",
                color: "oklch(0.08 0.03 240)",
                borderBottomRightRadius: "6px",
                boxShadow: "0 1px 4px oklch(0.82 0.07 75 / 35%)",
              }
            : {
                background: "white",
                color: "oklch(0.10 0.04 240)",
                border: "1.5px solid oklch(0.82 0.02 240)",
                borderBottomLeftRadius: "6px",
                boxShadow: "0 1px 3px oklch(0.60 0.02 240 / 15%)",
              }),
        }}
      >
        {/* Message text */}
        {text && <span>{text}</span>}

        {/* MMS filename badge */}
        {mmsFilename && (
          <span
            className="inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-lg text-xs font-semibold"
            style={{
              background: isOut ? "oklch(0.58 0.14 75 / 45%)" : "oklch(0.88 0.06 240)",
              color: isOut ? "oklch(0.08 0.03 240)" : "oklch(0.18 0.12 240)",
              border: isOut ? "1px solid oklch(0.52 0.14 75 / 55%)" : "1px solid oklch(0.72 0.10 240)",
              display: "flex",
            }}
          >
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            {mmsFilename}
          </span>
        )}

        {/* Media attachment */}
        {msg.mediaUrl && <MediaAttachment url={msg.mediaUrl} />}
      </div>

      {/* Timestamp */}
      <span className="mt-1 px-1" style={{ fontSize: "11px", fontWeight: 500, color: "oklch(0.48 0.03 240)" }}>
        {formatBubbleTime(msg.timestamp)}
        {isOut && (() => {
          // Extract [APXGS] or [HRBBS] from subject field for dual-number routing label
          const labelMatch = msg.subject?.match(/\[(APXGS|HRBBS)\]/);
          const label = labelMatch ? labelMatch[1] : null;
          if (label) {
            return (
              <span
                style={{
                  marginLeft: "6px",
                  padding: "1px 6px",
                  borderRadius: "4px",
                  fontSize: "10px",
                  fontWeight: 700,
                  letterSpacing: "0.04em",
                  background: label === "HRBBS" ? "oklch(0.88 0.10 300 / 60%)" : "oklch(0.88 0.10 240 / 60%)",
                  color: label === "HRBBS" ? "oklch(0.30 0.18 300)" : "oklch(0.25 0.18 240)",
                  border: `1px solid ${label === "HRBBS" ? "oklch(0.65 0.18 300 / 40%)" : "oklch(0.55 0.18 240 / 40%)"}`,
                }}
              >
                {label}
              </span>
            );
          }
          if (msg.source) {
            return (
              <span style={{ marginLeft: "6px", color: "oklch(0.55 0.04 240)" }}>
                · via {msg.source.includes("twilio") ? "Twilio" : "Textellent"}
              </span>
            );
          }
          return null;
        })()}
      </span>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function SmsInbox() {
  // Read ?phone= URL param to deep-link to a specific thread from notification click
  const initialPhone = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("phone") ?? "";
    if (!raw) return null;
    const digits = raw.replace(/\D/g, "");
    return digits.length >= 10 ? digits.slice(-10) : raw.toLowerCase();
  }, []);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [replyText, setReplyText] = useState("");
  const [isReplying, setIsReplying] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [replyAttachment, setReplyAttachment] = useState<{ file: File; previewUrl: string | null } | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const replyFileInputRef = useRef<HTMLInputElement>(null);
  const threadBottomRef = useRef<HTMLDivElement>(null);

  const { user } = useAuth();
  const canDelete = ["super_admin", "admin", "controller", "asst_controller"].includes(user?.role ?? "");

  // ─── Add Contact state ────────────────────────────────────────────────────
  const [addContactPhone, setAddContactPhone] = useState<string | null>(null);
  const [addContactName, setAddContactName] = useState("");
  const [addContactPerson, setAddContactPerson] = useState("");
  const [addContactSaving, setAddContactSaving] = useState(false);
  const createContactMut = trpc.communications.createContact.useMutation({
    onSuccess: () => {
      toast.success("Contact saved to directory");
      setAddContactPhone(null);
      setAddContactName("");
      setAddContactPerson("");
      setAddContactSaving(false);
      refetch();
    },
    onError: (err) => {
      toast.error(`Failed to save: ${err.message}`);
      setAddContactSaving(false);
    },
  });

  const { data: messages = [], isLoading, refetch, isFetching } = trpc.clientNotifications.smsCombined.useQuery(
    { limit: 500 },
    { refetchInterval: 15000 }
  );

  const { data: twilioStatus, isLoading: statusLoading } = trpc.communications.twilioStatus.useQuery(
    undefined,
    { refetchInterval: 60_000, staleTime: 30_000 }
  );

  const sendSmsMutation = trpc.communications.sendSms.useMutation({
    onSuccess: () => {
      toast.success(replyAttachment ? "MMS sent via Twilio" : "Reply sent via Twilio");
      setReplyText("");
      setReplyAttachment(null);
      setIsReplying(false);
      refetch();
    },
    onError: (err) => {
      toast.error(`Failed to send: ${err.message}`);
      setIsReplying(false);
    },
  });

  const uploadMmsFile = trpc.communications.uploadMmsFile.useMutation();

  const deleteSentMutation = trpc.clientNotifications.deleteMessage.useMutation({
    onSuccess: () => { toast.success("Message removed from WorkCenter"); refetch(); },
    onError: (err) => toast.error(`Delete failed: ${err.message}`),
  });

  const deleteReceivedMutation = trpc.clientNotifications.deleteReceived.useMutation({
    onSuccess: () => { toast.success("Message removed from WorkCenter"); refetch(); },
    onError: (err) => toast.error(`Delete failed: ${err.message}`),
  });

  const handleReplyAttachment = useCallback((file: File) => {
    const MAX = 4.5 * 1024 * 1024;
    if (file.size > MAX) { toast.error("File must be under 4.5 MB for MMS"); return; }
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/gif", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) { toast.error("Only JPEG, PNG, GIF, WebP, and PDF files are supported"); return; }
    const previewUrl = file.type.startsWith("image/") ? URL.createObjectURL(file) : null;
    setReplyAttachment({ file, previewUrl });
  }, []);

  // ─── Build conversations map ───────────────────────────────────────────────
  const conversations = useMemo<Conversation[]>(() => {
    const map = new Map<string, CombinedMessage[]>();

    (messages as CombinedMessage[]).forEach((msg) => {
      const raw = msg.phoneOrEmail || "";
      const digits = raw.replace(/\D/g, "");
      const key = digits.length >= 10 ? digits.slice(-10) : raw.toLowerCase();

      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(msg);
    });

    const convs: Conversation[] = [];
    map.forEach((msgs, key) => {
      const sorted = [...msgs].sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      const last = sorted[sorted.length - 1];
      const displayName = last.contactName || last.phoneOrEmail || "Unknown";
      const phone = last.phoneOrEmail;
      convs.push({
        contactKey: key,
        displayName,
        phone,
        messages: sorted,
        lastMessage: last,
        unreadCount: 0,
      });
    });

    convs.sort(
      (a, b) =>
        new Date(b.lastMessage.timestamp).getTime() -
        new Date(a.lastMessage.timestamp).getTime()
    );

    return convs;
  }, [messages]);

  // ─── Filter conversations by search ───────────────────────────────────────
  const filteredConversations = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.toLowerCase();
    return conversations.filter(
      (c) =>
        c.displayName.toLowerCase().includes(q) ||
        (c.phone || "").toLowerCase().includes(q) ||
        c.messages.some((m) => (m.body || "").toLowerCase().includes(q))
    );
  }, [conversations, search]);

  const selectedConv = filteredConversations.find((c) => c.contactKey === selectedKey) ?? null;

  // Auto-select: prefer thread matching ?phone= param, else first conversation
  useEffect(() => {
    if (!selectedKey && filteredConversations.length > 0) {
      if (initialPhone) {
        const match = filteredConversations.find((c) => c.contactKey === initialPhone);
        setSelectedKey(match ? match.contactKey : filteredConversations[0].contactKey);
      } else {
        setSelectedKey(filteredConversations[0].contactKey);
      }
    }
  }, [filteredConversations, selectedKey, initialPhone]);

  // Scroll thread to bottom when conversation changes or new messages arrive
  useEffect(() => {
    if (threadBottomRef.current) {
      threadBottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedKey, selectedConv?.messages.length]);

  const totalMessages = (messages as CombinedMessage[]).length;
  const sentCount = (messages as CombinedMessage[]).filter((m) => m.direction === "sent").length;
  const receivedCount = (messages as CombinedMessage[]).filter((m) => m.direction === "received").length;

  return (
    <DashboardLayout noPadding>
      <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "0", gap: "0", minHeight: 0 }}>

        {/* ── Twilio Status Bar ──────────────────────────────────────────────── */}
        {!statusLoading && twilioStatus && (
          <div
            className="flex items-center gap-3 px-6 py-2.5 flex-shrink-0 flex-wrap"
            style={{ background: "#EDE8E0", borderBottom: "1px solid #D4CCC0" }}
          >
            <div style={{
              width: "8px", height: "8px", borderRadius: "50%", flexShrink: 0,
              background: twilioStatus.status === "healthy" ? "#22c55e" : "#ef4444",
              boxShadow: `0 0 0 3px ${twilioStatus.status === "healthy" ? "rgba(34,197,94,0.25)" : "rgba(239,68,68,0.25)"}`,
            }} />
            <span style={{ fontWeight: 700, fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.06em", color: twilioStatus.status === "healthy" ? "#15803d" : "#b91c1c" }}>
              SMS {twilioStatus.status === "healthy" ? "Operational" : "Issue Detected"}
            </span>
            <span style={{ color: "#a89880", fontSize: "13px" }}>|</span>
            <span style={{ color: "#3d3328", fontSize: "14px", fontWeight: 600, fontFamily: "monospace" }}>{twilioStatus.phone ?? "No number"}</span>
            {(twilioStatus.recentSent > 0 || twilioStatus.recentErrors > 0) && (
              <>
                <span style={{ color: "#a89880", fontSize: "13px" }}>|</span>
                <span style={{ fontSize: "13px", color: "#7a6a58", fontWeight: 500 }}>
                  Today:&nbsp;
                  <span style={{ color: "oklch(0.72 0.18 142)", fontWeight: 700 }}>{twilioStatus.recentSent} delivered</span>
                  {twilioStatus.recentErrors > 0 && (<>,&nbsp;<span style={{ color: "oklch(0.72 0.18 25)", fontWeight: 700 }}>{twilioStatus.recentErrors} failed</span></>)}
                </span>
              </>
            )}
            <span style={{ marginLeft: "auto", fontSize: "13px", color: "#7a6a58", fontStyle: "italic" }}>
              All SMS/MMS are sent from (424) 678-1277
            </span>
          </div>
        )}

        {/* ── Page Header ────────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between flex-shrink-0 flex-wrap gap-3" style={{ padding: "12px 24px 0" }}>
          <div>
            <div className="flex items-center gap-3 mb-1.5">
              <span className="apx-phase-badge">Communications</span>
              <span
                className="text-sm font-bold px-3 py-1 rounded-full"
                style={{ background: "oklch(0.82 0.07 75 / 15%)", color: "oklch(0.40 0.14 75)", border: "1.5px solid oklch(0.82 0.07 75 / 40%)" }}
              >
                {conversations.length} conversation{conversations.length !== 1 ? "s" : ""}
              </span>
              <span className="text-base font-semibold" style={{ color: "#3d3328" }}>
                {sentCount} sent · {receivedCount} received · {totalMessages} total
              </span>
            </div>
            <h1
              className="text-xl font-bold"
              style={{ color: "#1a1208", fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.01em" }}
            >
              SMS Conversations
            </h1>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setComposeOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold transition-colors"
              style={{ background: "oklch(0.82 0.07 75)", border: "none", color: "oklch(0.08 0.03 240)", boxShadow: "0 2px 8px oklch(0.82 0.07 75 / 40%)" }}
            >
              <PenSquare size={14} />
              New Message
            </button>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
              style={{ background: "oklch(0.92 0.008 240)", border: "1.5px solid oklch(0.78 0.02 240)", color: "oklch(0.15 0.04 240)", opacity: isFetching ? 0.6 : 1 }}
            >
              <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* ── Main Two-Column Layout ─────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: "14px", flex: 1, minHeight: 0, padding: "0 24px 16px" }}>

          {/* ── Left: Contact / Conversation List ──────────────────────────── */}
          <div style={{
            width: "330px", minWidth: "270px", flexShrink: 0,
            display: "flex", flexDirection: "column", gap: "10px",
          }}>
            {/* Search box */}
            <div style={{ position: "relative", flexShrink: 0 }}>
              <Search size={15} style={{ position: "absolute", left: "12px", top: "50%", transform: "translateY(-50%)", color: "oklch(0.40 0.04 240)", pointerEvents: "none" }} />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search contacts or messages…"
                style={{
                  width: "100%", paddingLeft: "36px", paddingRight: "12px",
                  paddingTop: "10px", paddingBottom: "10px",
                  borderRadius: "10px", fontSize: "14px", fontWeight: 500, outline: "none",
                  background: "white",
                  border: "1.5px solid oklch(0.78 0.02 240)",
                  color: "oklch(0.10 0.04 240)",
                  boxSizing: "border-box",
                  boxShadow: "0 1px 3px oklch(0.60 0.02 240 / 10%)",
                }}
              />
            </div>

            {/* Conversation list */}
            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "5px" }}>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="rounded-xl animate-pulse" style={{ height: "80px", background: "oklch(0.90 0.008 240)" }} />
                ))
              ) : filteredConversations.length === 0 ? (
                <div className="rounded-xl p-8 text-center" style={{ background: "oklch(0.93 0.008 240)" }}>
                  <MessageSquare size={32} style={{ color: "oklch(0.55 0.04 240)", margin: "0 auto 10px" }} />
                  <p className="text-sm font-semibold" style={{ color: "oklch(0.28 0.04 240)" }}>
                    {search ? "No conversations match" : "No conversations yet"}
                  </p>
                </div>
              ) : (
                filteredConversations.map((conv) => {
                  const isSelected = selectedKey === conv.contactKey;
                  const { text: previewText, mmsFilename } = parseMmsBody(conv.lastMessage.body || "");
                  const isLastOut = conv.lastMessage.direction === "sent";
                  const preview = mmsFilename
                    ? (previewText ? `${previewText} [${mmsFilename}]` : `[MMS] ${mmsFilename}`)
                    : (previewText || (conv.lastMessage.mediaUrl ? "(media)" : "(no body)"));

                  return (
                    <div
                      key={conv.contactKey}
                      style={{ position: "relative" }}
                    >
                    {/* Add Contact button — only for unknown numbers */}
                    {conv.displayName === conv.phone && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setAddContactPhone(conv.phone);
                          setAddContactName("");
                          setAddContactPerson("");
                        }}
                        title="Add to Contacts"
                        style={{
                          position: "absolute", top: "8px", right: "8px",
                          background: "#B8922A", border: "none", borderRadius: "6px",
                          color: "#fff", cursor: "pointer",
                          display: "flex", alignItems: "center", gap: "4px",
                          fontSize: "11px", fontWeight: 700, padding: "3px 8px",
                          zIndex: 2, boxShadow: "0 1px 4px rgba(184,146,42,0.35)",
                        }}
                      >
                        <UserPlus size={11} /> Add
                      </button>
                    )}
                    <button
                      onClick={() => setSelectedKey(conv.contactKey)}
                      style={{
                        width: "100%", textAlign: "left", borderRadius: "12px", padding: "13px 15px",
                        background: isSelected ? "#EDE8E0" : "white",
                        border: isSelected ? "1.5px solid #B8922A" : "1.5px solid #e8e2da",
                        transition: "all 0.12s", cursor: "pointer",
                        boxShadow: isSelected ? "0 2px 10px rgba(184,146,42,0.15)" : "0 1px 3px rgba(0,0,0,0.05)",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "8px", marginBottom: "5px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                          {/* Avatar */}
                          <div style={{
                            width: "40px", height: "40px", borderRadius: "50%", flexShrink: 0,
                            background: isSelected ? "#B8922A" : "#7a6a58",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: "15px", fontWeight: 800,
                            color: "#fff",
                          }}>
                            {conv.displayName.charAt(0).toUpperCase()}
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <p style={{ fontSize: "15px", fontWeight: 800, color: "#1a1208", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {conv.displayName}
                            </p>
                            {conv.phone && conv.displayName !== conv.phone && (
                              <p style={{ fontSize: "12px", fontWeight: 600, color: isSelected ? "#7a6a58" : "#5a4e42", margin: 0, display: "flex", alignItems: "center", gap: "3px" }}>
                                <Phone size={10} />
                                {conv.phone}
                              </p>
                            )}
                          </div>
                        </div>
                        <div style={{ flexShrink: 0, textAlign: "right" }}>
                          <span style={{ fontSize: "12px", fontWeight: 700, color: isSelected ? "#7a6a58" : "#5a4e42" }}>
                            {formatTime(conv.lastMessage.timestamp)}
                          </span>
                          <p style={{ fontSize: "12px", fontWeight: 700, color: isSelected ? "#B8922A" : "#7a6a58", margin: "2px 0 0", textAlign: "right" }}>
                            {conv.messages.length} msg{conv.messages.length !== 1 ? "s" : ""}
                          </p>
                        </div>
                      </div>
                      <p style={{
                        fontSize: "13px", fontWeight: 500,
                        color: isSelected ? "#5a4e42" : "#3d3328",
                        margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        paddingLeft: "50px",
                      }}>
                        {isLastOut && <span style={{ color: isSelected ? "oklch(0.35 0.08 75)" : "oklch(0.42 0.10 75)", fontWeight: 700 }}>You: </span>}
                        {preview}
                      </p>
                    </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ── Right: Thread Panel ────────────────────────────────────────── */}
          <div style={{
            flex: 1, minWidth: 0,
            display: "flex", flexDirection: "column",
            background: "oklch(0.95 0.025 220)",
            border: "1.5px solid oklch(0.85 0.020 220)",
            borderRadius: "16px",
            overflow: "hidden",
            boxShadow: "0 2px 12px oklch(0.60 0.02 240 / 10%)",
          }}>
            {!selectedConv ? (
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px" }}>
                <MessageSquare size={52} style={{ color: "oklch(0.65 0.04 240)", marginBottom: "16px" }} />
                <p style={{ fontSize: "18px", fontWeight: 700, color: "oklch(0.20 0.04 240)", marginBottom: "6px" }}>
                  Select a conversation
                </p>
                <p style={{ fontSize: "14px", fontWeight: 500, color: "oklch(0.42 0.04 240)" }}>
                  {filteredConversations.length} conversation{filteredConversations.length !== 1 ? "s" : ""} available
                </p>
              </div>
            ) : (
              <>
                {/* Thread header */}
                <div style={{
                  padding: "10px 18px",
                  borderBottom: "1px solid #D4CCC0",
                  background: "#EDE8E0",
                  flexShrink: 0,
                  display: "flex", alignItems: "center", gap: "12px",
                }}>
                  <div style={{
                    width: "34px", height: "34px", borderRadius: "50%",
                    background: "#B8922A",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "14px", fontWeight: 800, color: "#fff", flexShrink: 0,
                  }}>
                    {selectedConv.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <h2 style={{ fontSize: "16px", fontWeight: 800, color: "#1a1208", margin: 0, fontFamily: "'DM Sans', sans-serif", letterSpacing: "-0.01em" }}>
                      {selectedConv.displayName}
                    </h2>
                    {selectedConv.phone && selectedConv.displayName !== selectedConv.phone && (
                      <p style={{ fontSize: "12px", fontWeight: 600, color: "#5a4e42", margin: 0, display: "flex", alignItems: "center", gap: "4px" }}>
                        <Phone size={11} />
                        {selectedConv.phone}
                      </p>
                    )}
                  </div>
                  <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: "16px" }}>
                    <span style={{ fontSize: "12px", fontWeight: 700, color: "#5a4e42" }}>
                      {selectedConv.messages.length} message{selectedConv.messages.length !== 1 ? "s" : ""}
                    </span>
                  </div>
                </div>

                {/* Chat bubble thread */}
                <div style={{ flex: 1, overflowY: "auto", padding: "22px 26px", background: "oklch(0.95 0.025 220)" }}>
                  {selectedConv.messages.map((msg, idx) => {
                    const prevMsg = idx > 0 ? selectedConv.messages[idx - 1] : null;
                    const msgDate = new Date(msg.timestamp).toDateString();
                    const prevDate = prevMsg ? new Date(prevMsg.timestamp).toDateString() : null;
                    const showDateSep = msgDate !== prevDate;

                    return (
                      <div key={msg.id}>
                        {showDateSep && (
                          <div style={{ display: "flex", alignItems: "center", gap: "12px", margin: "18px 0 14px" }}>
                            <div style={{ flex: 1, height: "1px", background: "oklch(0.82 0.015 240)" }} />
                            <span style={{ fontSize: "12px", fontWeight: 700, color: "oklch(0.40 0.04 240)", whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                              {new Date(msg.timestamp).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
                            </span>
                            <div style={{ flex: 1, height: "1px", background: "oklch(0.82 0.015 240)" }} />
                          </div>
                        )}
                        {/* Message row with optional delete button */}
                        <div
                          className="group"
                          style={{ position: "relative", display: "flex", flexDirection: "column", alignItems: msg.direction === "sent" ? "flex-end" : "flex-start" }}
                        >
                          <ChatBubble msg={msg} />
                          {canDelete && (
                            <button
                              onClick={() => {
                                if (!window.confirm("Remove this message from WorkCenter? (Twilio records are not affected.)")) return;
                                const numId = parseInt(msg.id.replace(/^(sent|recv)-/, ""), 10);
                                if (msg.direction === "sent") {
                                  deleteSentMutation.mutate({ id: numId });
                                } else {
                                  deleteReceivedMutation.mutate({ id: numId });
                                }
                              }}
                              title="Remove from WorkCenter (soft delete)"
                              className="transition-opacity"
                              style={{
                                position: "absolute",
                                top: "22px",
                                ...(msg.direction === "sent" ? { left: "-28px" } : { right: "-28px" }),
                                background: "none",
                                border: "none",
                                cursor: "pointer",
                                padding: "4px",
                                color: "oklch(0.55 0.18 25)",
                                borderRadius: "6px",
                              }}
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                  <div ref={threadBottomRef} />
                </div>

                {/* ── Reply Box ─────────────────────────────────────────────── */}
                {selectedConv.phone && (
                  <div
                    style={{
                      padding: "14px 20px 10px",
                      borderTop: "1.5px solid oklch(0.85 0.015 240)",
                      background: "white",
                      flexShrink: 0,
                      outline: isDragOver ? "2px dashed oklch(0.50 0.18 240)" : "none",
                      outlineOffset: "-4px",
                    }}
                    onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
                    onDragLeave={() => setIsDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setIsDragOver(false);
                      const file = e.dataTransfer.files[0];
                      if (file) handleReplyAttachment(file);
                    }}
                  >
                    {/* Attachment preview */}
                    {replyAttachment && (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px", padding: "8px 12px", borderRadius: "10px", background: "oklch(0.94 0.006 240)", border: "1.5px solid oklch(0.78 0.02 240)" }}>
                        {replyAttachment.previewUrl ? (
                          <img src={replyAttachment.previewUrl} alt="preview" style={{ width: 42, height: 42, objectFit: "cover", borderRadius: "6px", flexShrink: 0 }} />
                        ) : (
                          <div style={{ width: 42, height: 42, borderRadius: "6px", background: "oklch(0.88 0.01 240)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                            <FileText size={22} style={{ color: "oklch(0.42 0.14 25)" }} />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ fontSize: "13px", fontWeight: 700, color: "oklch(0.10 0.04 240)", margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{replyAttachment.file.name}</p>
                          <p style={{ fontSize: "12px", fontWeight: 500, color: "oklch(0.40 0.04 240)", margin: 0 }}>{(replyAttachment.file.size / 1024).toFixed(0)} KB · MMS</p>
                        </div>
                        <button onClick={() => setReplyAttachment(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "oklch(0.40 0.04 240)", padding: "4px" }}>
                          <X size={15} />
                        </button>
                      </div>
                    )}

                    {/* Drag hint */}
                    {!replyAttachment && isDragOver && (
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "8px", padding: "14px", marginBottom: "10px", borderRadius: "10px", background: "oklch(0.90 0.04 240 / 40%)", border: "2px dashed oklch(0.50 0.18 240)", color: "oklch(0.30 0.14 240)", fontSize: "13px", fontWeight: 600 }}>
                        <ImageIcon size={15} />
                        Drop image or PDF to attach
                      </div>
                    )}

                    {/* Input row */}
                    <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            if (replyText.trim() || replyAttachment) document.getElementById("sms-send-btn")?.click();
                          }
                        }}
                        placeholder={`Reply to ${selectedConv.displayName}… (Enter to send, Shift+Enter for newline)`}
                        rows={2}
                        style={{
                          flex: 1, borderRadius: "12px", padding: "11px 15px",
                          fontSize: "14px", fontWeight: 500, outline: "none", resize: "none",
                          background: "oklch(0.97 0.004 240)",
                          border: "1.5px solid oklch(0.78 0.02 240)",
                          color: "oklch(0.08 0.04 240)",
                          lineHeight: "1.55",
                        }}
                      />
                      <div style={{ display: "flex", flexDirection: "column", gap: "6px", flexShrink: 0 }}>
                        {/* Attach button */}
                        <button
                          onClick={() => replyFileInputRef.current?.click()}
                          title="Attach image or PDF (MMS)"
                          style={{
                            padding: "9px 11px", borderRadius: "10px", cursor: "pointer",
                            background: "oklch(0.88 0.02 240)", border: "1.5px solid oklch(0.74 0.02 240)",
                            color: "oklch(0.28 0.06 240)",
                          }}
                        >
                          <Paperclip size={15} />
                        </button>
                        {/* Send button */}
                        <button
                          id="sms-send-btn"
                          onClick={async () => {
                            if (!replyText.trim() && !replyAttachment) return;
                            setIsReplying(true);
                            try {
                              let mediaUrl: string | undefined;
                              if (replyAttachment) {
                                const base64 = await new Promise<string>((res, rej) => {
                                  const reader = new FileReader();
                                  reader.onload = () => res((reader.result as string).split(",")[1]);
                                  reader.onerror = rej;
                                  reader.readAsDataURL(replyAttachment.file);
                                });
                                const uploaded = await uploadMmsFile.mutateAsync({
                                  name: replyAttachment.file.name,
                                  mimeType: replyAttachment.file.type,
                                  size: replyAttachment.file.size,
                                  fileDataBase64: base64,
                                });
                                mediaUrl = uploaded.url;
                              }
                              // Append opt-out footer to every outbound message
                              const messageBody = replyText.trim()
                                ? replyText.trim() + OPT_OUT_FOOTER
                                : OPT_OUT_FOOTER.trim();
                              const replyFromLabel: "APXGS" | "HRBBS" = user?.role === "tax_division" ? "HRBBS" : "APXGS";
                              sendSmsMutation.mutate({
                                senderName: user?.name ?? "APX Team",
                                senderEmail: user?.email ?? "apxadvisor@gmail.com",
                                clientName: selectedConv.displayName,
                                clientPhone: selectedConv.phone!,
                                message: messageBody,
                                mediaUrl,
                                mediaFileName: replyAttachment?.file.name,
                                fromLabel: replyFromLabel,
                              });
                            } catch (err: unknown) {
                              toast.error(`Upload failed: ${err instanceof Error ? err.message : String(err)}`);
                              setIsReplying(false);
                            }
                          }}
                          disabled={(!replyText.trim() && !replyAttachment) || isReplying || uploadMmsFile.isPending}
                          style={{
                            padding: "9px 11px", borderRadius: "10px",
                            cursor: (replyText.trim() || replyAttachment) && !isReplying ? "pointer" : "not-allowed",
                            background: (replyText.trim() || replyAttachment) && !isReplying ? "oklch(0.82 0.07 75)" : "oklch(0.88 0.02 240)",
                            border: "none",
                            color: (replyText.trim() || replyAttachment) && !isReplying ? "oklch(0.08 0.03 240)" : "oklch(0.45 0.03 240)",
                            boxShadow: (replyText.trim() || replyAttachment) && !isReplying ? "0 2px 6px oklch(0.82 0.07 75 / 40%)" : "none",
                          }}
                        >
                          {uploadMmsFile.isPending ? "…" : isReplying ? "…" : <Send size={15} />}
                        </button>
                      </div>
                    </div>

                    {/* Footer hint */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "8px" }}>
                      <p style={{ fontSize: "12px", fontWeight: 500, color: "oklch(0.42 0.04 240)", margin: 0 }}>
                        {replyAttachment ? "MMS via Twilio" : "SMS via Twilio · Drag & drop image or PDF to send as MMS"}
                      </p>
                      <p style={{ fontSize: "11px", fontWeight: 600, color: "oklch(0.55 0.10 75)", margin: 0, fontStyle: "italic" }}>
                        ✓ "Reply STOP to opt-out" auto-appended
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <SmsComposeModal
        open={composeOpen}
        onClose={() => setComposeOpen(false)}
        senderName={user?.name ?? "APX Team"}
        senderEmail={user?.email ?? "apxadvisor@gmail.com"}
        userRole={user?.role}
      />

      {/* ── Add Contact Dialog ─────────────────────────────────────────────── */}
      {addContactPhone && (
        <div
          style={{
            position: "fixed", inset: 0, zIndex: 1000,
            background: "rgba(0,0,0,0.45)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
          onClick={() => setAddContactPhone(null)}
        >
          <div
            style={{
              background: "white", borderRadius: "16px",
              padding: "28px 28px 24px",
              width: "100%", maxWidth: "400px",
              boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div style={{
                  width: "36px", height: "36px", borderRadius: "50%",
                  background: "#B8922A", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <UserPlus size={16} color="white" />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: "16px", fontWeight: 800, color: "#1a1208" }}>Add Contact</p>
                  <p style={{ margin: 0, fontSize: "12px", fontWeight: 600, color: "#7a6a58" }}>Save to directory</p>
                </div>
              </div>
              <button onClick={() => setAddContactPhone(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#7a6a58" }}>
                <X size={18} />
              </button>
            </div>

            {/* Phone (pre-filled, read-only) */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#7a6a58", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: "5px" }}>Phone Number</label>
              <input
                readOnly
                value={addContactPhone}
                style={{
                  width: "100%", padding: "9px 12px", borderRadius: "8px",
                  border: "1.5px solid #e8e2da", background: "#f7f4f0",
                  fontSize: "14px", fontWeight: 600, color: "#5a4e42",
                  fontFamily: "'DM Mono', monospace", boxSizing: "border-box",
                }}
              />
            </div>

            {/* Organization Name */}
            <div style={{ marginBottom: "14px" }}>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#7a6a58", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: "5px" }}>Organization Name *</label>
              <input
                autoFocus
                value={addContactName}
                onChange={(e) => setAddContactName(e.target.value)}
                placeholder="e.g. Acme Corp"
                style={{
                  width: "100%", padding: "9px 12px", borderRadius: "8px",
                  border: "1.5px solid #D4CCC0", background: "white",
                  fontSize: "14px", fontWeight: 600, color: "#1a1208",
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>

            {/* Contact Person */}
            <div style={{ marginBottom: "22px" }}>
              <label style={{ display: "block", fontSize: "11px", fontWeight: 700, color: "#7a6a58", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: "5px" }}>Contact Person (optional)</label>
              <input
                value={addContactPerson}
                onChange={(e) => setAddContactPerson(e.target.value)}
                placeholder="e.g. John Smith"
                style={{
                  width: "100%", padding: "9px 12px", borderRadius: "8px",
                  border: "1.5px solid #D4CCC0", background: "white",
                  fontSize: "14px", fontWeight: 600, color: "#1a1208",
                  outline: "none", boxSizing: "border-box",
                }}
              />
            </div>

            {/* Actions */}
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setAddContactPhone(null)}
                style={{
                  flex: 1, padding: "10px", borderRadius: "8px",
                  border: "1.5px solid #D4CCC0", background: "white",
                  fontSize: "14px", fontWeight: 700, color: "#7a6a58", cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                disabled={!addContactName.trim() || addContactSaving}
                onClick={() => {
                  if (!addContactName.trim()) return;
                  setAddContactSaving(true);
                  createContactMut.mutate({
                    clientName: addContactName.trim(),
                    clientPhone: addContactPhone ?? undefined,
                    contactPerson: addContactPerson.trim() || undefined,
                    clientType: "non_xero",
                  });
                }}
                style={{
                  flex: 1, padding: "10px", borderRadius: "8px",
                  border: "none",
                  background: addContactName.trim() ? "#B8922A" : "#D4CCC0",
                  fontSize: "14px", fontWeight: 700, color: "white",
                  cursor: addContactName.trim() ? "pointer" : "not-allowed",
                  boxShadow: addContactName.trim() ? "0 2px 8px rgba(184,146,42,0.35)" : "none",
                }}
              >
                {addContactSaving ? "Saving…" : "Save Contact"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
